import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useAuth } from '@/hooks/auth-context';
import { useVaultVenues } from '@/hooks/useVaultVenues';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Button } from '@/components/ui/button';
import type { Venue, VenueStatus } from '@/types/venue';
import { getVenueStatusInfo } from '@/types/venue';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Area, AreaChart, XAxis, YAxis } from 'recharts';
import {
  Store,
  Plus,
  LayoutGrid,
  List,
  MapPin,
  Phone,
  Edit,
  ExternalLink,
  Clock,
  Loader2,
  AlertCircle,
  RefreshCw,
  Archive,
  Eye,
  FileEdit,
  X,
  ChevronDown,
  MessageSquare,
  CalendarCheck,
  TrendingUp,
  TrendingDown,
  User,
  LogOut,
  Home,
  Link2,
  AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ViewMode = 'grid' | 'list';

// Chart configuration for shadcn/ui charts
const conversationsChartConfig: ChartConfig = {
  conversations: {
    label: "Conversations",
    color: "hsl(217, 91%, 60%)", // Blue
  },
};

const reservationsChartConfig: ChartConfig = {
  reservations: {
    label: "Reservations",
    color: "hsl(142, 76%, 36%)", // Green
  },
};

// Dashboard metric card component with trend indicator (kept for future use)
interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  description?: string;
  trend?: number; // Percentage change
  loading?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _MetricCard = ({ title, value, icon, description, trend, loading }: MetricCardProps) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {loading ? (
          <div className="h-9 w-24 bg-gray-200 animate-pulse rounded mt-1" />
        ) : (
          <p className="text-3xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          {description && <p className="text-xs text-gray-500">{description}</p>}
          {trend !== undefined && trend !== 0 && (
            <span className={`flex items-center text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
              {Math.abs(trend).toFixed(1)}%
            </span>
          )}
        </div>
      </div>
      <div className="p-3 bg-blue-50 rounded-full">
        {icon}
      </div>
    </div>
  </div>
);

// Mini chart component for dashboard
interface MiniChartProps {
  data: { date: string; value: number }[];
  config: ChartConfig;
  dataKey: string;
  color: string;
  loading?: boolean;
}

const MiniChart = ({ data, config, dataKey, color, loading }: MiniChartProps) => {
  if (loading) {
    return (
      <div className="h-[120px] w-full bg-gray-100 animate-pulse rounded" />
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[120px] w-full flex items-center justify-center text-gray-400 text-sm">
        No data available
      </div>
    );
  }

  // Format data for chart - take last 14 days for mini chart
  const chartData = data.slice(-14).map(d => ({
    date: d.date,
    [dataKey]: d.value,
  }));

  return (
    <ChartContainer config={config} className="h-[120px] w-full">
      <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <defs>
          <linearGradient id={`fill-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => {
            const date = new Date(value);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }}
          tick={{ fontSize: 10 }}
          interval="preserveStartEnd"
        />
        <YAxis hide />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(value) => {
                return new Date(value).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                });
              }}
            />
          }
        />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#fill-${dataKey})`}
        />
      </AreaChart>
    </ChartContainer>
  );
};

// Status change confirmation dialog
interface StatusChangeDialogProps {
  isOpen: boolean;
  venue: Venue | null;
  newStatus: VenueStatus | null;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

const StatusChangeDialog = ({ isOpen, venue, newStatus, onConfirm, onCancel, isLoading }: StatusChangeDialogProps) => {
  if (!isOpen || !venue || !newStatus) return null;

  const statusInfo = getVenueStatusInfo(newStatus);
  const currentStatusInfo = getVenueStatusInfo(venue.status || 'draft');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Change Venue Status</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-600 mb-4">
          Are you sure you want to change the status of <strong>{venue.title}</strong> from{' '}
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${currentStatusInfo.color}`}>
            {currentStatusInfo.label}
          </span>{' '}
          to{' '}
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
            {statusInfo.label}
          </span>?
        </p>

        <p className="text-sm text-gray-500 mb-6">
          {statusInfo.description}
        </p>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Status dropdown component
interface StatusDropdownProps {
  venue: Venue;
  onStatusChange: (venue: Venue, newStatus: VenueStatus) => void;
}

const StatusDropdown = ({ venue, onStatusChange }: StatusDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentStatus = venue.status || 'draft'; // Default to draft if status not set
  const statusInfo = getVenueStatusInfo(currentStatus);

  const statuses: VenueStatus[] = ['draft', 'published', 'archived'];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${statusInfo.color} hover:opacity-80`}
      >
        {currentStatus === 'draft' && <FileEdit className="h-3 w-3" />}
        {currentStatus === 'published' && <Eye className="h-3 w-3" />}
        {currentStatus === 'archived' && <Archive className="h-3 w-3" />}
        {statusInfo.label}
        <ChevronDown className="h-3 w-3" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg border z-20">
            {statuses.map((status) => {
              const info = getVenueStatusInfo(status);
              const isCurrentStatus = status === currentStatus;
              return (
                <button
                  key={status}
                  onClick={() => {
                    if (!isCurrentStatus) {
                      onStatusChange(venue, status);
                    }
                    setIsOpen(false);
                  }}
                  disabled={isCurrentStatus}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 first:rounded-t-md last:rounded-b-md ${
                    isCurrentStatus ? 'bg-gray-50 cursor-default' : ''
                  }`}
                >
                  {status === 'draft' && <FileEdit className="h-4 w-4 text-yellow-600" />}
                  {status === 'published' && <Eye className="h-4 w-4 text-green-600" />}
                  {status === 'archived' && <Archive className="h-4 w-4 text-gray-600" />}
                  <span>{info.label}</span>
                  {isCurrentStatus && <span className="ml-auto text-xs text-gray-400">Current</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const AdminPage = () => {
  const { language } = useI18n();
  const navigate = useNavigate();
  useDocumentLang();

  // Auth check
  const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();

  // Venue management
  const {
    venues,
    loading: venuesLoading,
    error: venuesError,
    listVenues,
    updateVenue,
    clearError,
  } = useVaultVenues();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>(undefined);

  // Dashboard analytics - fetches from /api/analytics/summary
  const { data: analyticsData, loading: analyticsLoading } = useAnalytics({
    period: '30d',
    venueId: selectedVenueId
  });

  // Status change dialog state
  const [statusDialog, setStatusDialog] = useState<{
    isOpen: boolean;
    venue: Venue | null;
    newStatus: VenueStatus | null;
  }>({ isOpen: false, venue: null, newStatus: null });
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(`/${language}/login`);
    }
  }, [authLoading, isAuthenticated, navigate, language]);

  // Load venues when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadVenues();
    }
  }, [isAuthenticated]);

  const loadVenues = async () => {
    try {
      clearError();
      await listVenues();
    } catch (error) {
      console.error('Failed to load venues:', error);
    }
  };

  const formatHours = (venue: Venue) => {
    const openDays = venue.hours.filter(h => !h.isClosed);
    if (openDays.length === 0) return 'Closed';
    if (openDays.length === 7) return 'Open 7 days';
    return `Open ${openDays.length} days/week`;
  };

  // Status change handlers
  const handleStatusChangeRequest = (venue: Venue, newStatus: VenueStatus) => {
    setStatusDialog({ isOpen: true, venue, newStatus });
  };

  const handleStatusChangeConfirm = async () => {
    if (!statusDialog.venue || !statusDialog.newStatus) return;

    try {
      setStatusChangeLoading(true);
      await updateVenue(statusDialog.venue.id, { status: statusDialog.newStatus });
      await listVenues(); // Refresh the list
      setStatusDialog({ isOpen: false, venue: null, newStatus: null });
    } catch (error) {
      console.error('Failed to update venue status:', error);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleStatusChangeCancel = () => {
    setStatusDialog({ isOpen: false, venue: null, newStatus: null });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading while fetching venues
  if (venuesLoading && venues.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading venues...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (venuesError && venues.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load venues</h2>
          <p className="text-gray-600 mb-4">{venuesError}</p>
          <Button onClick={loadVenues}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Left: Logo/Brand */}
            <div className="flex items-center gap-6">
              <Link to={`/${language}/admin`} className="flex items-center gap-2 text-gray-900 hover:text-blue-600 transition-colors">
                <Store className="h-5 w-5" />
                <span className="font-semibold hidden sm:inline">Dashboard</span>
              </Link>
            </div>

            {/* Right: Actions + User Menu */}
            <div className="flex items-center gap-3">
              {/* Add Venue Button */}
              <Link to={`/${language}/edit/new`}>
                <Button size="sm">
                  <Plus className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Add Venue</span>
                </Button>
              </Link>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden md:inline max-w-[120px] truncate">
                      {user?.name || user?.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={`/${language}/`} className="cursor-pointer">
                      <Home className="mr-2 h-4 w-4" />
                      Home
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      <div className="container mx-auto px-4 max-w-7xl py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">My Venues</h1>
          <p className="text-gray-600 mt-1">Manage your restaurant venues and view analytics</p>
        </div>

        {/* Analytics Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Filter analytics by venue:</label>
            <select
              value={selectedVenueId || ''}
              onChange={(e) => setSelectedVenueId(e.target.value || undefined)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
            >
              <option value="">All Venues</option>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.title}
                </option>
              ))}
            </select>
            {selectedVenueId && (
              <button
                onClick={() => setSelectedVenueId(undefined)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>

        {/* Dashboard Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Conversations Card with Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Conversations</p>
                {analyticsLoading ? (
                  <div className="h-9 w-24 bg-gray-200 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {(analyticsData?.summary.totalConversations || 0).toLocaleString()}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-gray-500">Last 30 days</p>
                  {analyticsData?.summary.conversationsTrend !== undefined && analyticsData.summary.conversationsTrend !== 0 && (
                    <span className={`flex items-center text-xs font-medium ${analyticsData.summary.conversationsTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.summary.conversationsTrend > 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
                      {Math.abs(analyticsData.summary.conversationsTrend).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-full">
                <MessageSquare className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <MiniChart
              data={analyticsData?.conversationsByDay.data || []}
              config={conversationsChartConfig}
              dataKey="conversations"
              color="hsl(217, 91%, 60%)"
              loading={analyticsLoading}
            />
          </div>

          {/* Reservations Card with Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Reservations</p>
                {analyticsLoading ? (
                  <div className="h-9 w-24 bg-gray-200 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {(analyticsData?.summary.totalReservations || 0).toLocaleString()}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-gray-500">Made via AI concierge</p>
                  {analyticsData?.summary.reservationsTrend !== undefined && analyticsData.summary.reservationsTrend !== 0 && (
                    <span className={`flex items-center text-xs font-medium ${analyticsData.summary.reservationsTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsData.summary.reservationsTrend > 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
                      {Math.abs(analyticsData.summary.reservationsTrend).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-full">
                <CalendarCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <MiniChart
              data={analyticsData?.reservationsByDay.data || []}
              config={reservationsChartConfig}
              dataKey="reservations"
              color="hsl(142, 76%, 36%)"
              loading={analyticsLoading}
            />
          </div>
        </div>

        {/* View Toggle and Count */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {venues.length} {venues.length === 1 ? 'venue' : 'venues'}
            </div>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Venues Display */}
        {venues.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Store className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No venues yet</h3>
            <p className="text-gray-600 mb-6">Get started by adding your first venue</p>
            <Link to={`/${language}/edit/new`}>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Venue
              </Button>
            </Link>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <div key={venue.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{venue.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{venue.description}</p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <StatusDropdown venue={venue} onStatusChange={handleStatusChangeRequest} />
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Link2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      {venue.shortCode ? (
                        <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-700">
                          {venue.shortCode}
                        </code>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="text-xs">Legacy (no shortcode)</span>
                        </span>
                      )}
                      {/* LINE Bot status indicator */}
                      {venue.lineIntegration?.linked ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 rounded text-xs text-green-700" title="LINE Bot linked">
                          <MessageSquare className="h-3 w-3" />
                          LINE
                        </span>
                      ) : venue.shortCode ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-400" title="LINE Bot not linked">
                          <MessageSquare className="h-3 w-3" />
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-1">
                        {venue.address.city}, {venue.address.state}
                      </span>
                    </div>
                    {venue.contact.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>{venue.contact.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4 flex-shrink-0" />
                      <span>{formatHours(venue)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Link to={`/${language}/edit/${venue.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </Link>
                    <Link to={`/${language}/talk/${venue.shortCode || venue.id}/${venue.vanitySlug}`} className="flex-1">
                      <Button size="sm" className="w-full">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {venues.map((venue) => (
              <div key={venue.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{venue.title}</h3>
                      <StatusDropdown venue={venue} onStatusChange={handleStatusChangeRequest} />
                      {venue.shortCode ? (
                        <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-700">
                          {venue.shortCode}
                        </code>
                      ) : (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 rounded text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="text-xs">Legacy</span>
                        </span>
                      )}
                      {/* LINE Bot status indicator */}
                      {venue.lineIntegration?.linked && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 rounded text-xs text-green-700" title="LINE Bot linked">
                          <MessageSquare className="h-3 w-3" />
                          LINE
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4">{venue.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-start gap-2 text-gray-600">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <div>{venue.address.street}</div>
                          <div>
                            {venue.address.city}, {venue.address.state} {venue.address.postalCode}
                          </div>
                          <div>{venue.address.country}</div>
                        </div>
                      </div>

                      {venue.contact.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span>{venue.contact.phone}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="h-4 w-4 flex-shrink-0" />
                        <span>{formatHours(venue)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-6">
                    <Link to={`/${language}/edit/${venue.id}`}>
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </Link>
                    <Link to={`/${language}/talk/${venue.shortCode || venue.id}/${venue.vanitySlug}`}>
                      <Button size="sm">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Status Change Confirmation Dialog */}
        <StatusChangeDialog
          isOpen={statusDialog.isOpen}
          venue={statusDialog.venue}
          newStatus={statusDialog.newStatus}
          onConfirm={handleStatusChangeConfirm}
          onCancel={handleStatusChangeCancel}
          isLoading={statusChangeLoading}
        />

        {/* Footer with version */}
        <footer className="mt-8 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">v{import.meta.env.VITE_APP_VERSION}</p>
        </footer>
      </div>
    </div>
  );
};
